import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  FormControl,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import useOrgData from 'application/hooks/useOrgData';
import {
  FieldLabel,
  StyledButton,
  StyledTextField,
} from '../Configuration/configurationForms/styledFormComponents';
import { StyledSelect, StyledMenuItem } from './StyledSelect';
import { ImageUpload } from './ImageUpload';
import _ from 'lodash';
import { GamifyToast } from 'application/common/CustomToasts';
import { validateEmail, validatePhoneNumber } from '../../utils/validation';
import { useTheme } from '../../../../context/PermissionsContext/ThemeContext/ThemeContext';

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

const LEGAL_ENTITY_TYPES = [
  'Limited Liability Company (LLC)',
  'Corporation (C-Corp)',
  'Corporation (S-Corp)',
  'Sole Proprietorship',
  'Partnership',
];

export default function CompanyInformation() {
  const [org, setOrg] = useState<any>(null);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const { orgData, updateOrgData } = useOrgData();
  const { theme } = useTheme();
  const [errors, setErrors] = useState<{
    contactEmail?: string;
    contactPhone?: string;
    insuranceContactEmail?: string;
    insuranceContactPhone?: string;
  }>({});

  const validateFields = (): boolean => {
    const newErrors: {
      contactEmail?: string;
      contactPhone?: string;
      insuranceContactEmail?: string;
      insuranceContactPhone?: string;
    } = {};

    if (org?.contactEmail && !validateEmail(org.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }

    if (org?.contactPhone && !validatePhoneNumber(org.contactPhone)) {
      newErrors.contactPhone =
        'Please enter a valid phone number (at least 10 digits)';
    }

    if (
      org?.insuranceContactEmail &&
      !validateEmail(org.insuranceContactEmail)
    ) {
      newErrors.insuranceContactEmail = 'Please enter a valid email address';
    }

    if (
      org?.insuranceContactPhone &&
      !validatePhoneNumber(org.insuranceContactPhone)
    ) {
      newErrors.insuranceContactPhone =
        'Please enter a valid phone number (at least 10 digits)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (orgData) {
      setOrg(orgData);
    }
  }, [orgData]);

  useEffect(() => {
    if (!_.isEqual(org, orgData)) {
      setHasChanged(true);
    } else if (hasChanged) {
      setHasChanged(false);
    }
  }, [org]);

  const handleSubmit = async () => {
    if (!validateFields()) {
      GamifyToast.error('Please fix validation errors before saving');
      return;
    }

    const res = await updateOrgData({
      id: org.id,
      companyLogo: org.companyLogo,
      coverPhoto: org.coverPhoto,
      legalBusinessName: org.legalBusinessName,
      dbaName: org.dbaName,
      legalEntityType: org.legalEntityType,
      einTaxId: org.einTaxId,
      statesOfOperation: org.statesOfOperation,
      contactAddress: org.contactAddress,
      contactCity: org.contactCity,
      contactState: org.contactState,
      contactZip: org.contactZip,
      contactPhone: org.contactPhone,
      contactEmail: org.contactEmail,
      insuranceContactName: org.insuranceContactName,
      insuranceContactTitle: org.insuranceContactTitle,
      insuranceContactPhone: org.insuranceContactPhone,
      insuranceContactEmail: org.insuranceContactEmail,
      licenseNumber: org.licenseNumber,
      licenseIssuingState: org.licenseIssuingState,
      licenseExpirationDate: org.licenseExpirationDate,
      defaultEstimateDisclaimer: org.defaultEstimateDisclaimer,
      standardScopeNotes: org.standardScopeNotes,
    });
    if (res) {
      setHasChanged(false);
      GamifyToast.success('Company information updated successfully');
    } else {
      GamifyToast.error('Error updating company information');
    }
  };

  if (!org) {
    return <CircularProgress color="inherit" />;
  }

  const renderSection = (
    title: string,
    subtitle: string,
    children: React.ReactNode
  ) => (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{ fontWeight: 600, fontSize: 16, mb: 0.5, color: theme.text }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: 14, color: theme.textLight, mb: 2 }}>
        {subtitle}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.background1,
        borderRadius: '16px',
        marginBottom: 2,
      }}
    >
      <Typography sx={{ fontWeight: 780, fontSize: 20, color: theme.text }}>
        Company Information
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 15,
          color: theme.textLight,
          marginBottom: 3,
        }}
      >
        Manage your company branding, legal information, and contact details.
      </Typography>

      {renderSection(
        'Company Branding',
        'Upload your company logo and cover photo',
        <Box sx={{ gap: 3, display: 'flex', flexDirection: 'column' }}>
          <Box>
            <FieldLabel>Company Logo</FieldLabel>
            <ImageUpload
              value={org?.companyLogo}
              onChange={(imageKey) => setOrg({ ...org, companyLogo: imageKey })}
              label="Company Logo"
            />
          </Box>
          <Box>
            <FieldLabel>Cover Photo</FieldLabel>
            <ImageUpload
              value={org?.coverPhoto}
              onChange={(imageKey) => setOrg({ ...org, coverPhoto: imageKey })}
              label="Cover Photo"
            />
          </Box>
        </Box>
      )}

      {renderSection(
        'Legal Identity',
        "Your company's legal information",
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
          <Box>
            <FieldLabel>Legal Business Name</FieldLabel>
            <StyledTextField
              fullWidth
              value={org?.legalBusinessName || ''}
              onChange={(e) =>
                setOrg({ ...org, legalBusinessName: e.target.value })
              }
            />
          </Box>
          <Box>
            <FieldLabel>DBA Name (Optional)</FieldLabel>
            <StyledTextField
              fullWidth
              value={org?.dbaName || ''}
              onChange={(e) => setOrg({ ...org, dbaName: e.target.value })}
            />
          </Box>
          <Box>
            <FieldLabel>Legal Entity Type</FieldLabel>
            <FormControl fullWidth>
              <StyledSelect
                value={org?.legalEntityType || ''}
                onChange={(e) =>
                  setOrg({ ...org, legalEntityType: e.target.value })
                }
              >
                {LEGAL_ENTITY_TYPES.map((type) => (
                  <StyledMenuItem key={type} value={type}>
                    <Typography>{type}</Typography>
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Box>
          <Box>
            <FieldLabel>EIN / Tax ID</FieldLabel>
            <StyledTextField
              fullWidth
              type="text"
              value={org?.einTaxId || ''}
              onChange={(e) => setOrg({ ...org, einTaxId: e.target.value })}
            />
          </Box>
        </Box>
      )}

      {renderSection(
        'Address',
        'Your company physical address',
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
          <Box>
            <FieldLabel>Street Address</FieldLabel>
            <StyledTextField
              fullWidth
              value={org?.contactAddress || ''}
              onChange={(e) =>
                setOrg({ ...org, contactAddress: e.target.value })
              }
            />
          </Box>
          <Box>
            <FieldLabel>City</FieldLabel>
            <StyledTextField
              fullWidth
              value={org?.contactCity || ''}
              onChange={(e) => setOrg({ ...org, contactCity: e.target.value })}
            />
          </Box>
          <Box>
            <FieldLabel>State</FieldLabel>
            <FormControl fullWidth>
              <StyledSelect
                value={org?.contactState || ''}
                onChange={(e) =>
                  setOrg({ ...org, contactState: e.target.value })
                }
              >
                {US_STATES.map((state) => (
                  <StyledMenuItem key={state} value={state}>
                    <Typography>{state}</Typography>
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Box>
          <Box>
            <FieldLabel>Zipcode</FieldLabel>
            <StyledTextField
              fullWidth
              value={org?.contactZip || ''}
              onChange={(e) => setOrg({ ...org, contactZip: e.target.value })}
            />
          </Box>
        </Box>
      )}

      {renderSection(
        'General Company Contact',
        'Primary contact information for your company',
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
          <Box>
            <FieldLabel>Company Phone Number</FieldLabel>
            <StyledTextField
              fullWidth
              value={org?.contactPhone || ''}
              onChange={(e) => setOrg({ ...org, contactPhone: e.target.value })}
              error={!!errors.contactPhone}
              helperText={errors.contactPhone}
            />
          </Box>
          <Box>
            <FieldLabel>Company Email</FieldLabel>
            <StyledTextField
              fullWidth
              type="email"
              value={org?.contactEmail || ''}
              onChange={(e) => setOrg({ ...org, contactEmail: e.target.value })}
              error={!!errors.contactEmail}
              helperText={errors.contactEmail}
            />
          </Box>
        </Box>
      )}

      {renderSection(
        'Insurance/Adjuster Contact Information',
        'Contact information that appears on insurance estimates',
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
          <Box>
            <FieldLabel>Insurance Contact Name</FieldLabel>
            <StyledTextField
              fullWidth
              value={org?.insuranceContactName || ''}
              onChange={(e) =>
                setOrg({ ...org, insuranceContactName: e.target.value })
              }
            />
          </Box>
          <Box>
            <FieldLabel>Insurance Contact Title / Role</FieldLabel>
            <StyledTextField
              fullWidth
              value={org?.insuranceContactTitle || ''}
              onChange={(e) =>
                setOrg({ ...org, insuranceContactTitle: e.target.value })
              }
              placeholder="e.g., Claims Manager, Owner, Estimator"
            />
          </Box>
          <Box>
            <FieldLabel>Insurance Contact Phone</FieldLabel>
            <Typography sx={{ fontSize: 12, color: theme.textLight, mb: 1 }}>
              This phone number appears on insurance estimates and is used by
              adjusters and carriers to contact your company.
            </Typography>
            <StyledTextField
              fullWidth
              value={org?.insuranceContactPhone || ''}
              onChange={(e) =>
                setOrg({ ...org, insuranceContactPhone: e.target.value })
              }
              error={!!errors.insuranceContactPhone}
              helperText={errors.insuranceContactPhone}
            />
          </Box>
          <Box>
            <FieldLabel>Insurance Contact Email</FieldLabel>
            <Typography sx={{ fontSize: 12, color: theme.textLight, mb: 1 }}>
              This email appears on insurance estimates and is used by adjusters
              and carriers.
            </Typography>
            <StyledTextField
              fullWidth
              type="email"
              value={org?.insuranceContactEmail || ''}
              onChange={(e) =>
                setOrg({ ...org, insuranceContactEmail: e.target.value })
              }
              error={!!errors.insuranceContactEmail}
              helperText={errors.insuranceContactEmail}
            />
          </Box>
        </Box>
      )}

      {renderSection(
        'Licensing',
        'Your company license information',
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
          <Box>
            <FieldLabel>License Number</FieldLabel>
            <StyledTextField
              fullWidth
              value={org?.licenseNumber || ''}
              onChange={(e) =>
                setOrg({ ...org, licenseNumber: e.target.value })
              }
            />
          </Box>
          <Box>
            <FieldLabel>License Issuing State</FieldLabel>
            <FormControl fullWidth>
              <StyledSelect
                value={org?.licenseIssuingState || ''}
                onChange={(e) =>
                  setOrg({ ...org, licenseIssuingState: e.target.value })
                }
              >
                {US_STATES.map((state) => (
                  <StyledMenuItem key={state} value={state}>
                    <Typography>{state}</Typography>
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Box>
          <Box>
            <FieldLabel>License Expiration Date (Optional)</FieldLabel>
            <StyledTextField
              fullWidth
              type="date"
              value={org?.licenseExpirationDate || ''}
              onChange={(e) =>
                setOrg({ ...org, licenseExpirationDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      )}

      {renderSection(
        'Other Estimates Information (Optional)',
        'Default text that appears on your estimates',
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
          <Box>
            <FieldLabel>Default Estimate Disclaimer</FieldLabel>
            <StyledTextField
              fullWidth
              multiline
              rows={4}
              value={org?.defaultEstimateDisclaimer || ''}
              onChange={(e) =>
                setOrg({ ...org, defaultEstimateDisclaimer: e.target.value })
              }
            />
          </Box>
          <Box>
            <FieldLabel>Standard Scope Notes</FieldLabel>
            <StyledTextField
              fullWidth
              multiline
              rows={4}
              value={org?.standardScopeNotes || ''}
              onChange={(e) =>
                setOrg({ ...org, standardScopeNotes: e.target.value })
              }
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          mt: 2,
        }}
      >
        <StyledButton disabled={!hasChanged} onClick={handleSubmit}>
          Save Changes
        </StyledButton>
      </Box>
    </Paper>
  );
}
